// Pure, client-side helpers that keep a pedigree member array's denormalised
// edge arrays consistent with the per-child parent back-pointers AT
// CONSTRUCTION TIME. This is the front-end analogue of
// backend/util/pedigreeEdges.js (which only runs as a server safety net at
// persist time, children-only, and so cannot fix the live in-memory array nor
// I2/I4 violations).
//
// A member's parent<->child edge is stored twice:
//   - CHILD back-pointer:  fatherId / motherId   (SOURCE OF TRUTH)
//   - PARENT forward list: childrenIds
// and they must agree:
//   I1 forward: c.fatherId/motherId === P.pid  =>  P.childrenIds includes c.pid
//   I2 back:    c.pid in P.childrenIds          =>  c.fatherId===P.pid || c.motherId===P.pid
//   I4 sibling: members sharing >=1 parent are mutual entries in siblingIds
// The add-relative / set-relation paths build these by hand; these helpers
// derive both sides from a single decision so they cannot diverge.

const MALE = 'male';
const FEMALE = 'female';

const uniq = (arr) => [...new Set(arr)];

// Resolve a new child's parent back-pointers from the selected node and the
// chosen co-parent (the node's current-union spouse, or null). The node always
// takes its own sex slot; the spouse takes the opposite slot ONLY when its sex
// complements the node's (the data model's father/mother slots are sex-typed).
// `coParentId` is the spouse pid IFF the spouse actually occupies a slot — both
// the child's back-pointer AND the spouse's childrenIds entry must be derived
// from it, so a same-sex / unknown-sex spouse never ends up listing a child
// that does not point back to it (I2).
export const resolveChildParents = (nodeGender, ownerPid, spouse) => {
  if (nodeGender === MALE) {
    const coParentId = spouse && spouse.gender === FEMALE ? spouse.pid : null;
    return { fatherId: ownerPid, motherId: coParentId, coParentId };
  }
  const coParentId = spouse && spouse.gender === MALE ? spouse.pid : null;
  return { fatherId: coParentId, motherId: ownerPid, coParentId };
};

// Append newly-created sibling pids to the right existing members after adding
// sibling(s) of an existing node. A member can be BOTH a co-sibling (gets
// siblingIds) AND a shared parent (gets childrenIds) in a consanguinity loop,
// so the two updates are applied INDEPENDENTLY — never as a mutually-exclusive
// if/else, which would skip a parent's childrenIds when that parent is also
// classified a sibling (I1).
export const appendSiblingEdges = (
  members,
  { siblingGroupPids, fatherId, motherId, newSiblingPids }
) => {
  const group = new Set(siblingGroupPids);
  return members.map((person) => {
    const inGroup = group.has(person.pid);
    const isParent =
      (fatherId != null && person.pid === fatherId) ||
      (motherId != null && person.pid === motherId);
    if (!inGroup && !isParent) return person;
    const next = { ...person };
    if (inGroup) {
      next.siblingIds = uniq([...(person.siblingIds || []), ...newSiblingPids]);
    }
    if (isParent) {
      next.childrenIds = uniq([
        ...(person.childrenIds || []),
        ...newSiblingPids
      ]);
    }
    return next;
  });
};

// Next free numeric pid for a member array (collision-proof when called inside a
// setMemberRecords updater, where the array is always the latest state).
export const nextPidFrom = (members) => {
  let max = 0;
  for (const p of members || []) {
    if (typeof p.pid === 'number' && p.pid > max) max = p.pid;
  }
  return max + 1;
};

// PURE add-sibling(s) transform. Resolves the selected person and all parent /
// sibling / pid data FROM `members` (the array passed in), never from a captured
// closure — so rapid successive adds are correct regardless of React render
// timing (the bug where a fast second add read a stale selected-node and dropped
// the sibling at the parents' generation / with no parent edges). Call it as
// `setMemberRecords((prev) => addSiblingsToRecords(prev, selectedPid, opts))`.
//
// opts: { count=1, isTwin=false, siblingType, twinType, twinSubGroupId, makeSibling }
//   makeSibling() -> a fresh person object WITHOUT a pid (uid/gender/relationship
//   set, empty edge arrays); this function assigns sequential collision-free pids.
//   twinSubGroupId is supplied by the caller (kept out of here so the transform is
//   deterministic/testable).
export const addSiblingsToRecords = (
  members,
  selectedPid,
  {
    count = 1,
    isTwin = false,
    siblingType = null,
    twinType = null,
    twinSubGroupId = null,
    makeSibling
  } = {}
) => {
  const list = members || [];
  const self = list.find((p) => p.pid === selectedPid);
  if (!self || count < 1 || typeof makeSibling !== 'function') return list;

  // Fresh, sequential pids derived from the LATEST array.
  let pid = nextPidFrom(list);
  const newSiblings = [];
  for (let i = 0; i < count; i++) {
    const sib = makeSibling();
    sib.pid = pid++;
    sib.fatherId = self.fatherId ?? null;
    sib.motherId = self.motherId ?? null;
    newSiblings.push(sib);
  }
  const newSiblingPids = newSiblings.map((s) => s.pid);

  // Sibling group by SHARED PARENTS of the FRESH self (with the parentless
  // fallback to the explicit siblingIds, matching the single-add path).
  const noParents = self.fatherId == null && self.motherId == null;
  const groupPids = noParents
    ? uniq([self.pid, ...(self.siblingIds || [])])
    : list
        .filter((p) => {
          if (p.pid === self.pid) return true;
          const sameFather =
            self.fatherId != null && p.fatherId === self.fatherId;
          const sameMother =
            self.motherId != null && p.motherId === self.motherId;
          return sameFather || sameMother;
        })
        .map((p) => p.pid);

  // Twin fields stamped onto each new sibling (the existing self also joins the
  // twin sub-group below, after appendSiblingEdges so its siblingIds survive).
  const groupId = self.twinSubGroupId || twinSubGroupId || null;
  if (isTwin) {
    for (const sib of newSiblings) {
      sib.twinSubGroupId = groupId;
      sib.siblingType = siblingType;
      sib.twinRelations = [{ withPid: self.pid, type: twinType }];
    }
  }

  // I1/I4 edge arrays for the existing members (shared parents + sibling group).
  let arr = appendSiblingEdges(list, {
    siblingGroupPids: groupPids,
    fatherId: self.fatherId ?? null,
    motherId: self.motherId ?? null,
    newSiblingPids
  });

  // Augment the existing self with twin membership without losing its just-added
  // siblingIds.
  if (isTwin) {
    arr = arr.map((p) => {
      if (p.pid !== self.pid) return p;
      const next = { ...p, twinSubGroupId: groupId, siblingType };
      next.twinRelations = [
        ...(p.twinRelations || []),
        ...newSiblingPids.map((wp) => ({ withPid: wp, type: twinType }))
      ];
      return next;
    });
  }

  // New siblings reference the whole sibship (existing group + each other).
  for (const sib of newSiblings) {
    const ids = new Set(sib.siblingIds || []);
    for (const g of groupPids) if (g !== sib.pid) ids.add(g);
    for (const other of newSiblings)
      if (other.pid !== sib.pid) ids.add(other.pid);
    sib.siblingIds = [...ids];
  }

  // Insert immediately after the last existing member of the sibship (keeps the
  // new siblings adjacent to their group), else right after the selected person.
  let lastIdx = -1;
  arr.forEach((m, idx) => {
    if (groupPids.includes(m.pid) && idx > lastIdx) lastIdx = idx;
  });
  const insertIdx =
    lastIdx >= 0 ? lastIdx + 1 : arr.findIndex((m) => m.pid === self.pid) + 1;
  return [...arr.slice(0, insertIdx), ...newSiblings, ...arr.slice(insertIdx)];
};

// PURE add-child(ren) transform — same rationale as addSiblingsToRecords. The
// co-parent, the sex-typed parent slots, the existing-sibship, and the new pids
// are ALL resolved from the latest `members`, so rapid son/daughter adds can't
// read a stale selected-node or collide on pid. Call inside the updater:
// `setMemberRecords((prev) => addChildrenToRecords(prev, selectedPid, opts))`.
// opts: { count=1, makeChild, coParentPid } — makeChild() returns a fresh person
// WITHOUT a pid; coParentPid targets a specific union on a multi-spouse node
// (must be one of self.spouseIds; anything else falls back to the most recent
// spouse, the historical default).
export const addChildrenToRecords = (
  members,
  selectedPid,
  { count = 1, makeChild, coParentPid = null } = {}
) => {
  const list = members || [];
  const self = list.find((p) => p.pid === selectedPid);
  if (!self || count < 1 || typeof makeChild !== 'function') return list;

  const spouseId =
    coParentPid != null && self.spouseIds?.includes(coParentPid)
      ? coParentPid
      : self.spouseIds?.length
      ? self.spouseIds[self.spouseIds.length - 1]
      : null;
  const spouse = spouseId != null ? list.find((p) => p.pid === spouseId) : null;
  // coParentId is the spouse only when it actually occupies a parent slot; both
  // the child back-pointer AND the spouse's childrenIds derive from it (I1/I2).
  const { fatherId, motherId, coParentId } = resolveChildParents(
    self.gender,
    selectedPid,
    spouse
  );

  let pid = nextPidFrom(list);
  const newChildren = [];
  for (let i = 0; i < count; i++) {
    const child = makeChild();
    child.pid = pid++;
    child.fatherId = fatherId;
    child.motherId = motherId;
    newChildren.push(child);
  }
  const newChildIds = newChildren.map((c) => c.pid);

  const existingSibPids = new Set(
    list
      .filter((p) => p.fatherId === fatherId && p.motherId === motherId)
      .map((p) => p.pid)
  );

  const arr = list.map((person) => {
    if (person.pid === selectedPid) {
      return {
        ...person,
        childrenIds: uniq([...(person.childrenIds || []), ...newChildIds])
      };
    }
    if (coParentId != null && person.pid === coParentId) {
      return {
        ...person,
        childrenIds: uniq([...(person.childrenIds || []), ...newChildIds])
      };
    }
    if (existingSibPids.has(person.pid)) {
      return {
        ...person,
        siblingIds: uniq([...(person.siblingIds || []), ...newChildIds])
      };
    }
    return person;
  });

  for (const child of newChildren) {
    const ids = new Set(child.siblingIds || []);
    for (const s of existingSibPids) ids.add(s);
    for (const other of newChildren) {
      if (other.pid !== child.pid) ids.add(other.pid);
    }
    child.siblingIds = [...ids];
  }

  return [...arr, ...newChildren];
};

// Children of `node` whose OTHER parent slot is still empty — these become
// children of a newly added FIRST spouse too. Children that already have a real
// other parent must NOT be re-parented onto (or copied into the childrenIds of)
// the new spouse: that would steal the child from / orphan the existing parent's
// edge (I2). Returns [] when the node already has a spouse (no re-parenting).
export const adoptableChildrenForNewSpouse = (members, node) => {
  if (!node || (node.spouseIds && node.spouseIds.length > 0)) return [];
  const childIds = new Set(node.childrenIds || []);
  const otherSlot = node.gender === MALE ? 'motherId' : 'fatherId';
  return members
    .filter((p) => childIds.has(p.pid) && p[otherSlot] == null)
    .map((p) => p.pid);
};

// Pure transform for the drag-to-connect "Set Relation -> Child" path. Sets the
// child's missing parent back-pointer, keeps the chosen parent's (and, where
// applicable, that parent's first spouse's) childrenIds in lockstep, and — the
// fix — only lists the child under the spouse when the child actually
// back-points to that spouse (I2). Validation mirrors the original handler.
// Returns { error } | { members, touchedPids }.
export const linkChildRelation = (members, childPid, parentPid) => {
  const deepCopy = (members || []).map((m) => ({ ...m }));
  const childIdx = deepCopy.findIndex((p) => p.pid === childPid);
  const parentIdx = deepCopy.findIndex((p) => p.pid === parentPid);
  if (childIdx < 0 || parentIdx < 0) return { members: null };

  const child = { ...deepCopy[childIdx] };
  const parent = { ...deepCopy[parentIdx] };

  if (child.fatherId && child.motherId && child.noparents !== 'true') {
    return { error: 'Relation Not Allowed : Node already has parents' };
  }
  if (parent.gender === FEMALE) {
    if (child.motherId && child.motherId !== parent.pid) {
      return {
        error: 'Relation Not Allowed : Child already has a different mother'
      };
    }
    child.motherId = parent.pid;
  }
  if (parent.gender === MALE) {
    if (child.fatherId && child.fatherId !== parent.pid) {
      return {
        error: 'Relation Not Allowed : Child already has a different father'
      };
    }
    child.fatherId = parent.pid;
  }

  parent.childrenIds = uniq([...(parent.childrenIds || []), child.pid]);
  deepCopy[parentIdx] = parent;
  const touched = new Set([childPid, parentPid]);

  const spouseId = parent.spouseIds?.[0] ?? null;
  const spouseIdx =
    spouseId != null ? deepCopy.findIndex((p) => p.pid === spouseId) : -1;
  if (spouseIdx >= 0) {
    const spouse = { ...deepCopy[spouseIdx] };
    if (parent.gender === FEMALE && !child.fatherId) child.fatherId = spouseId;
    if (parent.gender === MALE && !child.motherId) child.motherId = spouseId;
    // Only claim the child under the spouse when the child actually back-points
    // to it — otherwise the spouse's childrenIds would gain an orphaned entry
    // (e.g. a child that already had its real other parent recorded). (I2)
    if (child.fatherId === spouseId || child.motherId === spouseId) {
      spouse.childrenIds = uniq([...(spouse.childrenIds || []), child.pid]);
      deepCopy[spouseIdx] = spouse;
      touched.add(spouseId);
    }
  }

  deepCopy[childIdx] = child;
  return { members: deepCopy, touchedPids: [...touched] };
};

// Pure transform for the drag-to-connect "Set Relation -> Sibling" path. Copies
// the parent back-pointers from whichever node already has parents onto the
// other, then — the fix — rebuilds childrenIds on the shared parents (I1) and
// siblingIds across the whole resulting sibship (I4); the original handler set
// only the back-pointers. Validation mirrors the original handler.
// Returns { error } | { members, touchedPids }.
export const linkSiblingRelation = (members, currentPid, siblingPid) => {
  const deepCopy = (members || []).map((m) => ({ ...m }));
  const currentIdx = deepCopy.findIndex((p) => p.pid === currentPid);
  const siblingIdx = deepCopy.findIndex((p) => p.pid === siblingPid);
  if (currentIdx < 0 || siblingIdx < 0) return { members: null };

  const current = { ...deepCopy[currentIdx] };
  const sibling = { ...deepCopy[siblingIdx] };

  if (
    (!sibling.motherId || sibling.noparents) &&
    (!current.motherId || current.noparents)
  ) {
    return {
      error: 'Relation Not Allowed : At least one node must have parents'
    };
  }
  if (
    current.motherId &&
    sibling.motherId &&
    current.motherId !== sibling.motherId
  ) {
    return {
      error: 'Relation Not Allowed : Both nodes have different parents'
    };
  }

  let parentSource = null;
  if (sibling.motherId && !sibling.noparents) parentSource = sibling;
  else if (current.motherId && !current.noparents) parentSource = current;

  if (parentSource) {
    current.motherId = parentSource.motherId;
    current.fatherId = parentSource.fatherId;
    sibling.motherId = parentSource.motherId;
    sibling.fatherId = parentSource.fatherId;
  }

  delete current.top_level;
  delete current.noparents;
  delete sibling.top_level;
  delete sibling.noparents;

  deepCopy[currentIdx] = current;
  deepCopy[siblingIdx] = sibling;

  const touched = new Set([currentPid, siblingPid]);
  const fatherId = parentSource ? parentSource.fatherId : null;
  const motherId = parentSource ? parentSource.motherId : null;

  // I1: each shared parent must list both children.
  [fatherId, motherId].forEach((parentPid) => {
    if (parentPid == null) return;
    const idx = deepCopy.findIndex((p) => p.pid === parentPid);
    if (idx < 0) return;
    const parent = { ...deepCopy[idx] };
    parent.childrenIds = uniq([
      ...(parent.childrenIds || []),
      currentPid,
      siblingPid
    ]);
    deepCopy[idx] = parent;
    touched.add(parentPid);
  });

  // I4: every member sharing >=1 parent with the new sibship is a mutual sibling.
  if (fatherId != null || motherId != null) {
    const sibPids = deepCopy
      .filter(
        (p) =>
          (fatherId != null && p.fatherId === fatherId) ||
          (motherId != null && p.motherId === motherId)
      )
      .map((p) => p.pid);
    sibPids.forEach((pid) => {
      const idx = deepCopy.findIndex((p) => p.pid === pid);
      const member = { ...deepCopy[idx] };
      member.siblingIds = uniq([
        ...(member.siblingIds || []),
        ...sibPids.filter((s) => s !== pid)
      ]);
      deepCopy[idx] = member;
      touched.add(pid);
    });
  }

  return { members: deepCopy, touchedPids: [...touched] };
};

// Remove a partner entry from a spouseRelationships map (object keyed by spouse
// pid). Returns the SAME reference when there is nothing to remove (so callers
// can cheaply detect a no-op), otherwise a new object without that key.
export const omitSpouseRelationship = (spouseRelationships, partnerPid) => {
  if (!spouseRelationships || spouseRelationships[partnerPid] == null) {
    return spouseRelationships;
  }
  const next = { ...spouseRelationships };
  delete next[partnerPid];
  return next;
};

// Scrub every reference to `pid` from a SURVIVING member after `pid` is deleted:
// null the parent back-pointers, drop it from the edge arrays, and drop the
// dangling auxiliary references (twinRelations[].withPid + the spouse-keyed
// spouseRelationships entry) so nothing keeps pointing at the removed member.
// Pure — returns a new member object.
export const stripMemberReferencesToPid = (member, pid) => {
  const next = {
    ...member,
    motherId: member.motherId === pid ? null : member.motherId,
    fatherId: member.fatherId === pid ? null : member.fatherId,
    spouseIds: (member.spouseIds || []).filter((id) => id !== pid),
    childrenIds: (member.childrenIds || []).filter((id) => id !== pid),
    siblingIds: (member.siblingIds || []).filter((id) => id !== pid)
  };
  if (Array.isArray(member.twinRelations)) {
    next.twinRelations = member.twinRelations.filter((r) => r.withPid !== pid);
  }
  const sr = omitSpouseRelationship(member.spouseRelationships, pid);
  if (sr !== member.spouseRelationships) next.spouseRelationships = sr;
  return next;
};

// Read-only integrity analyser (mirror of backend analyzeEdges) — exported for
// tests/dev assertions. Returns the violated edges by category.
export const analyzeMemberEdges = (members) => {
  const list = Array.isArray(members) ? members : [];
  const byPid = new Map();
  list.forEach((m) => {
    if (m && m.pid != null) byPid.set(m.pid, m);
  });

  const desired = new Map(); // parentPid -> Set<childPid> from back-pointers
  list.forEach((m) => {
    if (!m || m.pid == null) return;
    ['fatherId', 'motherId'].forEach((role) => {
      const pp = m[role];
      if (pp == null || !byPid.has(pp)) return;
      if (!desired.has(pp)) desired.set(pp, new Set());
      desired.get(pp).add(m.pid);
    });
  });

  const forwardMissing = [];
  const backMissing = [];
  const garbageChildren = [];
  const spouseAsymmetry = [];

  list.forEach((m) => {
    if (!m || m.pid == null) return;
    const have = new Set((m.childrenIds || []).filter((x) => x != null));
    const want = desired.get(m.pid) || new Set();
    want.forEach((c) => {
      if (!have.has(c)) forwardMissing.push({ parentPid: m.pid, childPid: c });
    });
    have.forEach((c) => {
      if (!byPid.has(c))
        garbageChildren.push({ parentPid: m.pid, childPid: c });
      else if (!want.has(c))
        backMissing.push({ parentPid: m.pid, childPid: c });
    });
    (m.spouseIds || []).forEach((b) => {
      const other = byPid.get(b);
      if (other && !(other.spouseIds || []).includes(m.pid)) {
        spouseAsymmetry.push({ pid: m.pid, spousePid: b });
      }
    });
  });

  return { forwardMissing, backMissing, garbageChildren, spouseAsymmetry };
};
